<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { ASSIGN_TYPES } from '../../../constants/csmlFieldOptions';

const props = defineProps({
  node: { type: Object, required: true },
});

const emit = defineEmits(['patch']);

const { t } = useI18n();

// Team and agent ids used to be typed in by hand, which meant a wrong id
// only surfaced once the bot ran. Both lists are already in the store.
const store = useStore();
const teams = useMapGetter('teams/getTeams');
const agents = useMapGetter('agents/getAgents');

store.dispatch('teams/get');
store.dispatch('agents/get');

const assignTypeOptions = ASSIGN_TYPES.map(option => ({
  value: option.value,
  label: t(`AGENT_BOTS.VISUAL_BUILDER.${option.labelKey}`),
}));

// A script imported from another install can reference an id this account
// does not have. Keeping it in the list is what makes it visible: a Select
// renders nothing for a value it has no option for, so the id the bot is
// actually configured with would silently look unset.
const withStoredValue = (options, storedId) => {
  const value = String(storedId || '');
  if (!value || options.some(option => option.value === value)) return options;
  return [
    ...options,
    {
      value,
      label: t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.UNKNOWN_ID', { value }),
    },
  ];
};

const teamOptions = computed(() =>
  withStoredValue(
    (teams.value || []).map(team => ({
      value: String(team.id),
      label: team.name,
    })),
    props.node.teamId
  )
);

const agentOptions = computed(() =>
  withStoredValue(
    (agents.value || []).map(agent => ({
      value: String(agent.id),
      label: agent.name,
    })),
    props.node.agentId
  )
);

const isAgent = computed(() => props.node.assignType === 'agent');
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-1">
      <span class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.ASSIGN_TYPE') }}
      </span>
      <Select
        :model-value="node.assignType || 'team'"
        :options="assignTypeOptions"
        @update:model-value="emit('patch', { assignType: $event })"
      />
    </div>
    <div v-if="!isAgent" class="flex flex-col gap-1">
      <span class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.TEAM') }}
      </span>
      <Select
        :model-value="String(node.teamId || '')"
        :options="teamOptions"
        :placeholder="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.TEAM_PLACEHOLDER')"
        @update:model-value="emit('patch', { teamId: $event })"
      />
      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <Switch
          :model-value="!!node.assignAvailableAgent"
          @update:model-value="emit('patch', { assignAvailableAgent: $event })"
        />
        {{ t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.ASSIGN_AVAILABLE_AGENT') }}
      </label>
    </div>
    <div v-else class="flex flex-col gap-1">
      <span class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.AGENT') }}
      </span>
      <Select
        :model-value="String(node.agentId || '')"
        :options="agentOptions"
        :placeholder="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.AGENT_PLACEHOLDER')"
        @update:model-value="emit('patch', { agentId: $event })"
      />
    </div>
    <TextArea
      :model-value="node.content"
      :label="t('AGENT_BOTS.VISUAL_BUILDER.CONTENT')"
      @update:model-value="emit('patch', { content: $event })"
    />
  </div>
</template>
